import { Transition } from "@headlessui/react";
import { MobileDrawer } from "@jugl-web/ui-components/mobile/MobileDrawer";
import { cx, useAppVariant } from "@jugl-web/utils";
import {
  ReactNode,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, ButtonProps } from "../Button";
import { PlainButton } from "../PlainButton";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

interface CTAPopupProps {
  isOpen: boolean;
  /**
   * Whether the close button should be shown (applies only to web)
   */
  hasCloseButton?: boolean;
  icon: ReactNode;
  title: ReactNode;
  content: ReactNode;
  buttons: {
    label: ReactNode;
    color?: ButtonProps["color"];
    onClick: () => void | Promise<void>;
  }[];
  onClose: () => void;
  onAfterClose?: () => void;
}

export interface CTAPopupHandle {
  /**
   * Trigger the bounce animation (applies only to web)
   */
  bounce: () => void;
}

export const CTAPopup = forwardRef<CTAPopupHandle, CTAPopupProps>(
  (
    {
      isOpen,
      hasCloseButton,
      icon,
      title,
      content,
      buttons,
      onClose,
      onAfterClose,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);

    const popupRef = useRef<HTMLDivElement | null>(null);

    const { isMobile } = useAppVariant();

    // #region Public API
    const isAnimatingRef = useRef(false);

    const handleBounce = () => {
      popupRef.current?.classList.add("animate-natural-bounce");
      isAnimatingRef.current = true;
    };

    const handleAnimationEnd = () => {
      popupRef.current?.classList.remove("animate-natural-bounce");
      isAnimatingRef.current = false;
    };

    useImperativeHandle(ref, () => ({ bounce: handleBounce }));
    // #endregion

    const asyncOnClickWrapper =
      (onClick: () => void | Promise<void>) => async () => {
        setIsLoading(true);
        await onClick();
        setIsLoading(false);
      };

    const dialogContent = (
      <>
        <div className="mb-2 flex items-center justify-between gap-1">
          <span className="font-base font-secondary leading-4 font-semibold text-dark">
            <span className="mr-1">{icon}</span> <span>{title}</span>
          </span>
          {hasCloseButton && !isMobile && (
            <PlainButton
              onClick={onClose}
              className="shrink-0 rounded transition-colors hover:bg-grey-100"
            >
              <CloseIcon />
            </PlainButton>
          )}
        </div>
        <div className="font-sm font-secondary leading-[150%] text-dark-800">
          {content}
        </div>
        {buttons.length > 0 && (
          <div className={cx("mt-8 flex items-center justify-center gap-2.5")}>
            {buttons.map(({ label, color, onClick }, index) => (
              <Button
                key={+index}
                color={color}
                fullWidth
                onClick={asyncOnClickWrapper(onClick)}
                isDisabled={isLoading}
                className="h-10 max-w-[300px]"
              >
                {label}
              </Button>
            ))}
          </div>
        )}
      </>
    );

    if (isMobile) {
      return (
        <MobileDrawer
          isOpen={isOpen}
          onClose={onClose}
          onTransitionEnd={() => {
            if (!isOpen) {
              onAfterClose?.();
            }
          }}
          className="px-4 pb-8"
          header={{ title: "", hasCloseButton: false }}
        >
          {dialogContent}
        </MobileDrawer>
      );
    }

    return (
      <Transition
        ref={popupRef}
        as="div"
        onAnimationEnd={handleAnimationEnd}
        afterLeave={onAfterClose}
        show={isOpen}
        enter="transition"
        enterFrom="opacity-0 -translate-x-4 scale-75"
        enterTo="opacity-100 translate-x-0 scale-100"
        leave="transition"
        leaveFrom="opacity-100 translate-x-0 scale-100"
        leaveTo="opacity-0 -translate-x-4 scale-75"
        className={cx(
          "util-prevent-drawer-close fixed right-10 bottom-10 z-[200] origin-bottom-right duration-300",
          "flex max-h-[100vh] min-h-[223px] w-[348px] flex-col rounded-2xl bg-white p-6 shadow-[0_24px_56px_0_rgba(0,0,0,0.16)]"
        )}
      >
        {dialogContent}
      </Transition>
    );
  }
);
